import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowsClockwise, Question } from "@phosphor-icons/react";

export const SapBulkPush = () => {
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [globalCC, setGlobalCC] = useState("P2-NPD");
  const [ccOverrides, setCcOverrides] = useState({});
  const [release, setRelease] = useState(true);
  const [pushing, setPushing] = useState(false);
  const [current, setCurrent] = useState(null);
  const [results, setResults] = useState({});

  const load = () => api.get("/projects/sap/unpushed").then((r) => setProjects(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === projects.length) setSelected(new Set());
    else setSelected(new Set(projects.map((p) => p.id)));
  };

  const pushAll = async () => {
    if (!globalCC.trim() && Object.values(ccOverrides).some((v) => !v?.trim())) return;
    setPushing(true);
    const res = { ...results };
    let ok = 0, fail = 0;
    for (const p of projects.filter((p) => selected.has(p.id))) {
      const cc = (ccOverrides[p.id] || globalCC).trim();
      if (!cc) { res[p.id] = { error: "No cost centre" }; fail++; continue; }
      setCurrent(p.id);
      try {
        const { data } = await api.post(`/projects/${p.id}/push-to-sap`, { cost_centre: cc, release });
        res[p.id] = data;
        ok++;
      } catch (e) {
        res[p.id] = { error: e.response?.data?.detail?.toString() || "Push failed" };
        fail++;
      }
      setResults({ ...res });
    }
    setCurrent(null);
    setPushing(false);
    setSelected(new Set());
    toast[fail ? "warning" : "success"](`SAP bulk push done — ${ok} created${fail ? `, ${fail} failed` : ""}`);
    load();
  };

  const pushedRows = projects.filter((p) => results[p.id]?.sap_project_id);

  return (
    <div data-testid="sap-bulk-push" className="border border-border bg-card" style={{ borderRadius: "2px" }}>
      <div className="p-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-semibold text-sm">Bulk Create in SAP
            <Link to="/help/sap-push" data-testid="sap-help-link" className="ml-2 text-xs font-normal underline text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
              <Question size={12} /> Work Instruction
            </Link>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">Push existing projects (with phases &amp; tasks) into SAP ByDesign. Already-pushed projects are excluded automatically.</p>
        </div>
        <button data-testid="sap-refresh-btn" onClick={load} className="btn-outline-navy flex items-center gap-2 !py-1.5 !px-3 text-xs">
          <ArrowsClockwise size={13} /> Refresh
        </button>
      </div>

      <div className="p-4 border-b border-border flex items-center gap-4 flex-wrap">
        <div>
          <label className="text-xs font-semibold block mb-1">Cost Centre for all</label>
          <div className="flex gap-2">
            {["P1-NPD", "P2-NPD"].map((cc) => (
              <button key={cc} type="button" onClick={() => setGlobalCC(cc)}
                className={`text-xs px-2 py-1 border ${globalCC === cc ? "bg-foreground text-background border-foreground" : "border-border hover:bg-muted"}`}
                style={{ borderRadius: "2px" }}>{cc}</button>
            ))}
            <input data-testid="sap-bulk-cc-input" value={globalCC} onChange={(e) => setGlobalCC(e.target.value)}
              className="w-28 border border-border px-2 py-1 text-sm bg-background" style={{ borderRadius: "2px" }} />
          </div>
        </div>
        <label className="flex items-center gap-2 text-xs cursor-pointer select-none mt-4">
          <input data-testid="sap-bulk-release-checkbox" type="checkbox" checked={release} onChange={(e) => setRelease(e.target.checked)} />
          Release immediately (Start &amp; Release)
        </label>
        <button data-testid="sap-bulk-push-btn" className="btn-navy mt-3 ml-auto disabled:opacity-50"
          disabled={pushing || selected.size === 0} onClick={pushAll}>
          {pushing ? `Pushing... (${Object.keys(results).length}/${selected.size})` : `Push ${selected.size} Selected to SAP`}
        </button>
      </div>

      <div className="max-h-96 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-card">
            <tr className="border-b border-border text-left text-xs text-muted-foreground">
              <th className="p-2 pl-4"><input type="checkbox" data-testid="sap-select-all" checked={projects.length > 0 && selected.size === projects.length} onChange={toggleAll} /></th>
              <th className="p-2">Project</th>
              <th className="p-2">Status</th>
              <th className="p-2">Phases / Tasks</th>
              <th className="p-2">Cost Centre</th>
              <th className="p-2">Result</th>
            </tr>
          </thead>
          <tbody>
            {projects.map((p) => {
              const r = results[p.id];
              return (
                <tr key={p.id} data-testid={`sap-row-${p.id}`} className={`border-b border-border ${current === p.id ? "bg-amber-50 dark:bg-amber-500/10" : ""}`}>
                  <td className="p-2 pl-4"><input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} disabled={pushing} /></td>
                  <td className="p-2 font-medium">{p.name}{p.archived && <span className="ml-1 text-[10px] text-muted-foreground">(archived)</span>}
                    {p.owner_name && <div className="text-[10px] text-muted-foreground">{p.owner_name}</div>}</td>
                  <td className="p-2 text-xs">{p.status}</td>
                  <td className="p-2 text-xs font-mono">{p.phase_count} / {p.task_count}</td>
                  <td className="p-2">
                    <input value={ccOverrides[p.id] ?? ""} placeholder={globalCC} disabled={pushing}
                      onChange={(e) => setCcOverrides({ ...ccOverrides, [p.id]: e.target.value })}
                      className="w-24 border border-border px-1.5 py-0.5 text-xs bg-background" style={{ borderRadius: "2px" }} />
                  </td>
                  <td className="p-2 text-xs">
                    {current === p.id && <span className="text-amber-600">Pushing…</span>}
                    {r?.sap_project_id && (
                      <span className="text-emerald-700 dark:text-emerald-400 font-mono">
                        {r.sap_project_id} · {r.sap_status} ({r.phases_created}ph/{r.tasks_created}t){r.errors?.length ? ` · ${r.errors.length} item err` : ""}
                      </span>
                    )}
                    {r?.error && <span className="text-red-600">{r.error}</span>}
                  </td>
                </tr>
              );
            })}
            {projects.length === 0 && (
              <tr><td colSpan={6} className="p-4 text-sm font-mono text-muted-foreground">&gt; all projects are already in SAP (or none published yet)</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {pushedRows.length > 0 && (
        <div className="p-3 border-t border-border text-xs text-muted-foreground">
          {pushedRows.length} project(s) created in SAP this session. Rows stay visible until you refresh.
        </div>
      )}
    </div>
  );
};
